import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './Stories.css';

// Константная длительность изображений (7 сек)
const IMAGE_DURATION = 7000;

/**
 * Компонент для просмотра историй в полноэкранном режиме
 * Отвечает за отображение контента историй и навигацию между ними
 */
const StoryViewer = ({ 
  activeStory, 
  activeItems, 
  onClose, 
  getFullUrl 
}) => {
  // Состояние просмотра историй
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  
  // Состояние прогресса
  const [progress, setProgress] = useState([]);
  const [disableTransition, setDisableTransition] = useState(false);
  
  // Состояние взаимодействия
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [wasLongPressWithoutMovement, setWasLongPressWithoutMovement] = useState(false);
  
  // Ссылки
  const storyContainerRef = useRef(null);
  const videoRef = useRef(null);
  const progressInterval = useRef(null);
  const timeoutRef = useRef(null);
  const longPressTimeoutRef = useRef(null);
  
  // Данные о взаимодействии
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchEndRef = useRef({ x: 0, y: 0, time: 0 });
  
  // Защита от быстрых повторных тапов и iOS фантомных кликов
  const processingTapRef = useRef(false);
  const navigationInProgressRef = useRef(false);
  const startItemIndexRef = useRef(0);
  const wasLongPressRef = useRef(false); // Флаг для отслеживания долгих нажатий
  const lastLongPressEndTimeRef = useRef(0); // Время окончания последнего долгого нажатия
  
  // Функция для очистки прогресс-таймера
  const clearProgressTimer = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  }, []);
  
  // Инициализация прогресса при открытии историй
  useEffect(() => {
    if (activeItems && activeItems.length > 0) {
      setProgress(activeItems.map(() => 0));
      setActiveItemIndex(0);
      startItemIndexRef.current = 0;
    }
    
    return () => {
      clearProgressTimer();
      clearTimeout(timeoutRef.current);
      clearTimeout(longPressTimeoutRef.current);
    };
  }, [activeStory, activeItems, clearProgressTimer]);
  
  // Управление прогрессом для текущего элемента истории
  useEffect(() => {
    if (!activeStory || isPaused) return;
    
    clearProgressTimer();
    
    const currentItem = activeItems[activeItemIndex];
    if (!currentItem) return;
    
    // Если текущий элемент - изображение, используем автоматический прогресс-бар
    if (currentItem.type === 'image') {
      const duration = IMAGE_DURATION;
      const increment = 100 / (duration / 100); // % увеличения каждые 100мс
      
      progressInterval.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = [...prev];
          
          if (newProgress[activeItemIndex] < 100) {
            newProgress[activeItemIndex] = Math.min(100, newProgress[activeItemIndex] + increment);
          } else {
            clearProgressTimer();
            
            // Переходим к следующему элементу с небольшой задержкой
            timeoutRef.current = setTimeout(() => {
              if (activeItemIndex < activeItems.length - 1) {
                setActiveItemIndex(prevIndex => prevIndex + 1);
              } else {
                onClose();
              }
            }, 50);
          }
          
          return newProgress;
        });
      }, 100);
    }
    
    return () => clearProgressTimer();
  }, [activeStory, activeItemIndex, isPaused, activeItems, clearProgressTimer, onClose]);
  
  // Функции навигации
  const prevStoryItem = useCallback(() => {
    // Минимальная защита от множественных срабатываний одного касания
    if (navigationInProgressRef.current) {
      return;
    }
    
    if (activeItemIndex > 0) {
      navigationInProgressRef.current = true;
      
      clearProgressTimer();
      
      // Отключаем анимацию для мгновенного переключения
      setDisableTransition(true);
      
      // Мгновенно устанавливаем прогресс без анимации
      const newProgress = [...progress];
      // Сбрасываем текущий элемент
      newProgress[activeItemIndex] = 0;
      // Сбрасываем предыдущий элемент
      newProgress[activeItemIndex - 1] = 0;
      // Заполняем все элементы до предыдущего
      for (let i = 0; i < activeItemIndex - 1; i++) {
        newProgress[i] = 100;
      }
      setProgress(newProgress);
      
      setActiveItemIndex(prev => prev - 1);
      
      // Включаем анимацию обратно через небольшую задержку
      setTimeout(() => {
        setDisableTransition(false);
      }, 50);
      
      // Сбрасываем блокировку через минимальное время
      setTimeout(() => {
        navigationInProgressRef.current = false;
      }, 200);
    }
  }, [activeItemIndex, progress, clearProgressTimer]);
  
  const nextStoryItem = useCallback(() => {
    // Минимальная защита от множественных срабатываний одного касания
    if (navigationInProgressRef.current) {
      return;
    }
    
    if (activeItemIndex < activeItems.length - 1) {
      navigationInProgressRef.current = true;
      
      clearProgressTimer();
      
      // Отключаем анимацию для мгновенного переключения
      setDisableTransition(true);
      
      // Мгновенно устанавливаем прогресс без анимации
      const newProgress = [...progress];
      // Заполняем текущий элемент до 100%
      newProgress[activeItemIndex] = 100;
      // Сбрасываем следующий элемент
      newProgress[activeItemIndex + 1] = 0;
      setProgress(newProgress);
      
      setActiveItemIndex(prev => prev + 1);
      
      // Включаем анимацию обратно через небольшую задержку
      setTimeout(() => {
        setDisableTransition(false);
      }, 50);
      
      // Сбрасываем блокировку через минимальное время
      setTimeout(() => {
        navigationInProgressRef.current = false;
      }, 200);
    } else {
      onClose();
    }
  }, [activeItemIndex, activeItems.length, progress, clearProgressTimer, onClose]);
  
  // Обработчики сенсорного ввода
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    
    // Сбрасываем флаг долгого нажатия
    wasLongPressRef.current = false;
    
    // Записываем время и координаты начала касания
    const touch = e.touches ? e.touches[0] : e;
    const currentTime = Date.now();
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: currentTime
    };
    
    // Записываем текущий индекс для отслеживания множественных переключений
    startItemIndexRef.current = activeItemIndex;
    
    // Сбрасываем перетаскивание и флаг долгого нажатия
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
    setWasLongPressWithoutMovement(false);
    
    // Ставим на паузу
    setIsPaused(true);
    
    // Для видео - приостанавливаем воспроизведение
    if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') {
      videoRef.current.pause();
    }
    
    // Устанавливаем таймер для определения долгого нажатия
    longPressTimeoutRef.current = setTimeout(() => {
      wasLongPressRef.current = true; // Устанавливаем флаг долгого нажатия
      setWasLongPressWithoutMovement(true); // Устанавливаем флаг долгого нажатия без движения
      // Долгое нажатие обнаружено, ничего дополнительно не делаем,
      // просто поддерживаем паузу до отпускания
    }, 200);
  }, [activeItemIndex, activeItems]);
  
  const handleTouchMove = useCallback((e) => {
    if (!isPaused) {
      return;
    }
    
    const touch = e.touches ? e.touches[0] : e;
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // Если перемещение значительное, очищаем таймер долгого нажатия
    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      clearTimeout(longPressTimeoutRef.current);
    }
    
    // Начинаем драг при значительном вертикальном перемещении
    if (Math.abs(deltaY) > 15 && !isDragging) {
      setIsDragging(true);
    }
    
    // Обновляем позицию только при активном перетаскивании
    if (isDragging) {
      setDragOffset({
        x: deltaX,
        y: deltaY
      });
    }
  }, [isPaused, isDragging]);
  
  const handleTouchEnd = useCallback((e) => {
    // Очищаем таймер долгого нажатия, если он был
    clearTimeout(longPressTimeoutRef.current);
    
    const touch = e.changedTouches ? e.changedTouches[0] : e;
    const currentTime = Date.now();
    touchEndRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: currentTime
    };
    
    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = touchEndRef.current.y - touchStartRef.current.y;
    const touchDuration = touchEndRef.current.time - touchStartRef.current.time;

    const localWasLongPress = wasLongPressRef.current; // Запоминаем, было ли это долгое нажатие
    const localWasLongPressWithoutMovement = wasLongPressWithoutMovement; // Запоминаем флаг долгого нажатия без движения
    
    wasLongPressRef.current = false; // Сразу сбрасываем флаг для следующих взаимодействий

    // 1. Обработка перетаскивания (вертикальный свайп для закрытия)
    if (isDragging) {
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
      if (deltaY > 100) {
        onClose();
        return;
      }
      // Если перетаскивание не привело к закрытию, просто снимаем паузу
      setIsPaused(false);
      if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') {
        videoRef.current.play().catch(err => console.error('[Video] Ошибка при воспроизведении видео:', err));
      }
      return; 
    }

    // 2. Если это было отпускание после долгого нажатия (localWasLongPress === true)
    if (localWasLongPress) {
      // Записываем время окончания долгого нажатия для защиты от фантомных касаний
      lastLongPressEndTimeRef.current = currentTime;
      
      // Проверяем, не является ли это также свайпом
      if (Math.abs(deltaX) > 60 && Math.abs(deltaY) < 40) { // Горизонтальный свайп
        if (deltaX > 0) {
          prevStoryItem();
        } else {
          nextStoryItem();
        }
      }
      // Долгое нажатие без свайпа - просто снимаем паузу, не переключаем историю
      setIsPaused(false);
      if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') {
        videoRef.current.play().catch(err => console.error('[Video] Ошибка при воспроизведении видео:', err));
      }
      return; 
    }

    // 3. Если это НЕ было долгое нажатие (localWasLongPress === false)
    // Обработка обычных свайпов (не после долгого нажатия)
    if (Math.abs(deltaX) > 60 && Math.abs(deltaY) < 40) { // !isDragging уже проверено выше
      if (deltaX > 0) {
        prevStoryItem();
      } else {
        nextStoryItem();
      }
      setIsPaused(false); 
      if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') {
         videoRef.current.play().catch(err => console.error('[Video] Ошибка:', err));
      }
      return;
    }
    
    // 4. Обработка обычных коротких тапов (не долгое нажатие, не свайп)
    // Проверяем, что это действительно короткий тап (не более 200мс) и НЕ было долгого нажатия
    const timeSinceLastLongPress = currentTime - lastLongPressEndTimeRef.current;
    
    if (!localWasLongPress && !localWasLongPressWithoutMovement && touchDuration < 200 && Math.abs(deltaX) < 20 && Math.abs(deltaY) < 20 && timeSinceLastLongPress > 300) {
      if (activeItemIndex !== startItemIndexRef.current) {
        setIsPaused(false); 
        if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') videoRef.current.play().catch(err => console.error(err));
        return;
      }
      
      if (!storyContainerRef.current) {
        setIsPaused(false); 
        if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') videoRef.current.play().catch(err => console.error(err));
        return;
      }
      
      const rect = storyContainerRef.current.getBoundingClientRect();
      const { width } = rect;
      const clickPosition = touch.clientX - rect.left;
      
      if (clickPosition < width / 3) {
        prevStoryItem();
      } else if (clickPosition > (width * 2) / 3) {
        nextStoryItem();
      }
      
      setIsPaused(false); 
      if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') {
        videoRef.current.play().catch(err => console.error('[Video] Ошибка:', err));
      }
      return;
    }

    // 5. Если ни одно из условий выше не выполнилось
    setIsPaused(false);
    if (videoRef.current && activeItems[activeItemIndex]?.type === 'video') {
      videoRef.current.play().catch(err => {
        console.error('[Video] Ошибка при воспроизведении видео:', err);
      });
    }
    processingTapRef.current = false; 
    
    // Сбрасываем flаг долгого нажатия без движения в конце
    setWasLongPressWithoutMovement(false);
    
  }, [onClose, prevStoryItem, nextStoryItem, isDragging, activeItems, activeItemIndex, wasLongPressWithoutMovement]);
  
  if (!activeStory) return null;
  
  return ReactDOM.createPortal(
    <div className="story-viewer">
      <div 
        className="story-viewer-background" 
        onClick={onClose}
        style={{
          opacity: isDragging ? Math.max(0, 1 - Math.abs(dragOffset.y) / 400) : 1,
          transition: isDragging ? 'none' : 'opacity 0.2s ease'
        }}
      ></div>
      <div 
        className="story-viewer-container"
        ref={storyContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseMove={(e) => e.buttons && handleTouchMove(e)}
        onMouseUp={handleTouchEnd}
        onContextMenu={(e) => e.preventDefault()}
        style={{ 
          transform: isDragging ? `translate3d(0, ${dragOffset.y}px, 0)` : 'translate3d(0, 0, 0)',
          opacity: isDragging ? Math.max(0, 1 - Math.abs(dragOffset.y) / 400) : 1,
          transition: isDragging ? 'none' : 'transform 0.2s ease, opacity 0.2s ease'
        }}
      >
        {/* Индикаторы прогресса */}
        <div className="story-progress-container">
          {activeItems.map((_, index) => (
            <div 
              key={index}
              className={`story-progress-bar ${
                activeItemIndex === index ? 'active' : 
                activeItemIndex > index ? 'completed' : ''
              }`}
            >
              <div 
                className={`story-progress-fill ${disableTransition ? 'no-transition' : ''}`}
                style={{width: `${progress[index]}%`}}
              ></div>
            </div>
          ))}
        </div>
        
        {/* Основной контент истории */}
        <div className="story-content">
          {(() => {
            const currentItem = activeItems[activeItemIndex];
            
            if (!currentItem) return null;
            
            if (currentItem.type === 'video') {
              return (
                <div className="video-container">
                  <video
                    ref={videoRef}
                    src={getFullUrl(currentItem.url)}
                    className="story-media"
                    autoPlay
                    playsInline
                    muted={false}
                    onLoadedMetadata={(e) => {
                      // Рассчитываем прогресс на основе текущего времени и длительности
                      const videoDuration = e.target.duration;
                      
                      // Устанавливаем обработчик для обновления прогресса
                      e.target.ontimeupdate = () => {
                        if (isPaused) return;
                        
                        const currentTime = e.target.currentTime;
                        const percent = (currentTime / videoDuration) * 100;
                        
                        setProgress(prev => {
                          const newProgress = [...prev];
                          newProgress[activeItemIndex] = percent;
                          return newProgress;
                        });
                      };
                    }}
                    onEnded={() => {
                      // Когда видео закончилось, переходим к следующему элементу
                      nextStoryItem();
                    }}
                    onError={(e) => {
                      console.error('Ошибка загрузки видео:', e);
                      // Автоматически переходим к следующему элементу при ошибке
                      nextStoryItem();
                    }}
                  />
                </div>
              );
            } else {
              return (
                <img
                  src={getFullUrl(currentItem.url)}
                  alt={`Элемент истории ${activeItemIndex + 1}`}
                  className="story-media"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/350?text=Ошибка+загрузки+изображения';
                  }}
                />
              );
            }
          })()}
        </div>
        
        {/* Невидимые области для навигации */}
        <div className="story-navigation">
          <div className="nav-button prev"></div>
          <div className="nav-button next"></div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StoryViewer;
